/**
 * Operator tags on a reservation — a controlled vocabulary the team applies by
 * hand from the admin, orthogonal to `status`.
 *
 * Status answers "where is this booking in its lifecycle". A tag answers
 * "what should we know about it that the lifecycle cannot say" — most
 * importantly that a confirmed table is not actually going to be sat.
 *
 * Deliberately a fixed list rather than free text. Free tags drift ("no show",
 * "noshow", "NO SHOW") and stop being countable within weeks, and
 * `likely-no-show` in particular drives money — see `billsCommission`.
 *
 * Stored on `reservations.tags` as canonical ids (not localized); the UI
 * renders `labelKey` through i18n.
 */
export interface ReservationTag {
  readonly id: string;
  /** English label. The admin row menu is English-literal, like its neighbours. */
  readonly label: string;
  /** i18n key, for surfaces that localize. */
  readonly labelKey: string;
  /**
   * Whether a reservation carrying this tag still earns us commission.
   *
   * `false` on `likely-no-show`: we bill venues per head actually seated, so a
   * table we believe was never sat must not sit in the revenue figure waiting
   * to be invoiced. Applying the tag zeroes `commission`; removing it
   * recomputes from the activity's own rate.
   */
  readonly billsCommission: boolean;
}

export const RESERVATION_TAGS: readonly ReservationTag[] = [
  {
    id: "likely-no-show",
    label: "Likely no-show",
    labelKey: "reservations.tag.likely-no-show",
    billsCommission: false,
  },
] as const;

export const RESERVATION_TAG_IDS: readonly string[] = RESERVATION_TAGS.map((t) => t.id);

export type ReservationTagId = (typeof RESERVATION_TAGS)[number]["id"];

/** The tag that suppresses commission. Named so call sites read as intent. */
export const LIKELY_NO_SHOW_TAG = "likely-no-show";

/**
 * Does this set of tags suppress commission?
 *
 * The single place that answers the question, so the booking path, the admin
 * edit screen and the MCP backfill can never disagree about whether a row
 * should be billed.
 */
export function tagsSuppressCommission(tags: readonly string[] | null | undefined): boolean {
  if (!tags?.length) return false;
  return RESERVATION_TAGS.some((t) => !t.billsCommission && tags.includes(t.id));
}
